// Admin-only widget: shows the newest human signups so the founder can send
// a personal welcome email in the first 24h (30-50% response rate).
// Copy-buttons + prefilled mailto make outreach one tap.
import { useEffect, useMemo, useState } from "react";
import { UserPlus, Mail, Loader2, Copy, ExternalLink, Sparkles, Users } from "lucide-react";
import { getAdminRecentSignups } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const relTime = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 86400 * 7) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const firstName = (name, email) => {
  if (name) return name.split(/\s+/)[0];
  return (email || "").split("@")[0].split(/[._]/)[0];
};

const WELCOME_TEMPLATE = (name) =>
  `Hi ${name},\n\nNicole here — founder of Lectura. Thanks so much for signing up! I wanted to reach out personally since you're one of our first users.\n\nWhat are you studying? I'd love to know what brought you to Lectura, and if there's anything specific I can help make better for you.\n\nIf Lectura ends up saving you time or helping a grade go up, I'd love a one-line quote — I'll upgrade you to Pro for life in exchange.\n\nHave a great day,\nNicole\ngetlectura.app`;

const buildMailto = (email, name) => {
  const first = firstName(name, email);
  const subject = encodeURIComponent(`Welcome to Lectura, ${first} 👋`);
  const body = encodeURIComponent(WELCOME_TEMPLATE(first));
  return `mailto:${email}?subject=${subject}&body=${body}`;
};

const PlanPill = ({ plan, sub }) => {
  if (plan === "pro") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
        <Sparkles className="h-2.5 w-2.5" /> Pro{sub ? ` · ${sub}` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-secondary text-muted-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
      Free
    </span>
  );
};

const RANGES = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
];

export const AdminSignupsCard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    getAdminRecentSignups(days, 100)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isAdmin, days]);

  const proCount = useMemo(
    () => (data?.signups || []).filter((s) => s.plan === "pro").length,
    [data]
  );

  if (!isAdmin) return null;

  const signups = data?.signups || [];

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied", { description: text });
    } catch {
      toast.error("Couldn't copy — long-press to select manually.");
    }
  };

  return (
    <div data-testid="admin-signups-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex flex-wrap items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <UserPlus className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl font-bold">Recent signups</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Newest humans on Lectura. Send a personal welcome in the first 24h — it converts.
          </p>
        </div>
        <div className="flex rounded-full border border-border p-1 text-xs">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              data-testid={`signups-range-${r.days}d`}
              className={`px-3 py-1 rounded-full font-semibold transition ${
                days === r.days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-6 grid place-items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-accent" />
        </div>
      ) : signups.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No new signups in the last {days === 1 ? "24 hours" : `${days} days`}.
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl border border-border bg-secondary/40 p-3">
              <p className="font-serif text-2xl font-bold">{signups.length}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">Signups</p>
            </div>
            <div className="rounded-xl border border-border bg-secondary/40 p-3">
              <p className="font-serif text-2xl font-bold">{proCount}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">On Pro</p>
            </div>
            <div className="rounded-xl border border-border bg-secondary/40 p-3">
              <p className="font-serif text-2xl font-bold">
                {signups.length > 0 ? Math.round((proCount / signups.length) * 100) : 0}%
              </p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">Conversion</p>
            </div>
          </div>

          <ul className="mt-5 divide-y divide-border/60 border-y border-border/60">
            {signups.map((s) => (
              <li key={s.user_id} data-testid={`signup-row-${s.user_id}`} className="py-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">
                      {s.name || firstName(null, s.email)}
                    </p>
                    <PlanPill plan={s.plan} sub={s.subscription_status} />
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{s.email}</p>
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    {relTime(s.created_at)} · {s.auth_provider === "google" ? "Google" : "Email"} · {s.lecture_count} lecture{s.lecture_count === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => copy(s.email)}
                    data-testid={`signup-copy-${s.user_id}`}
                    title="Copy email"
                    className="grid place-items-center h-8 w-8 rounded-lg border border-border hover:bg-secondary/60 transition"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href={buildMailto(s.email, s.name)}
                    data-testid={`signup-email-${s.user_id}`}
                    title={`Send welcome email to ${firstName(s.name, s.email)}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold hover:opacity-90 transition"
                  >
                    <Mail className="h-3.5 w-3.5" /> Email
                    <ExternalLink className="h-3 w-3 opacity-70" />
                  </a>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> "Email" opens your email app with a personal welcome template
            prefilled. Edit before sending — the personal touch is the whole point.
          </p>
        </>
      )}
    </div>
  );
};

export default AdminSignupsCard;
